// `MERGE … WHEN NOT MATCHED THEN INSERT (…) VALUES (▮` — the one VALUES list
// whose operands come from ANOTHER relation.
//
// Everywhere else a VALUES element is a literal or an expression over
// constants, so the generic values arm's bare column emission is right. Inside
// a MERGE the element is written against the USING source (`VALUES (src.id,
// src.film_id, …)`), and two things follow:
//
//   1. the QUALIFIED form is what the writer types, so `src.id` has to be an
//      offered label — a bare `id` completion makes the reader do the
//      qualification by hand (pgsql script.49 §4);
//   2. a bare reference to a name the TARGET also carries is an error, not a
//      shortcut — PG answers `column reference "film_id" is ambiguous`, T-SQL
//      `Ambiguous column name`. That is exactly the value-slot policy's D9
//      rule (`valueSlot/emitValueSlotCandidates.ts`): a duplicated name emits
//      one qualified candidate per owner and suppresses the bare form.
//
// MERGE is shared by PostgreSQL (15+), T-SQL and Oracle, and all three spell
// this clause the same way, so the policy lives here and each resolver hands
// in its own DISCOVERY (which bindings are in scope, what columns they carry,
// how the dialect quotes an identifier) — the same split the value-slot
// adapters use.

import type { AliasBinding, CompletionCandidate, DatabaseEngineId } from '@/core/engines';
import { tokenize, type Token } from '../context/tokenizer';

function identUpper(t: Token | undefined): string | null {
  if (!t || (t.type !== 'ident' && t.type !== 'kw')) return null;
  return t.text.toUpperCase();
}

/**
 * True when the caret sits inside the `VALUES ( … )` element list of a MERGE
 * statement's `WHEN NOT MATCHED THEN INSERT` arm.
 *
 * Shape check only — the enclosing unmatched `(` must be introduced by the
 * `VALUES` keyword, and the statement must open with `MERGE`. The INSERT
 * column list (`INSERT (▮`) therefore does NOT match: its paren is introduced
 * by `INSERT`, which is what keeps the column-list caret (script.49 §3, whose
 * spec forbids `src.*`) on today's behaviour.
 */
export function isAtMergeValuesElement(
  source: string,
  statementStart: number,
  statementEnd: number,
  cursor: number,
  engine: DatabaseEngineId,
): boolean {
  const tokens = tokenize(source, statementStart, statementEnd, engine);
  if (identUpper(tokens[0]) !== 'MERGE') return false;

  let cursorTokenIdx = -1;
  for (let i = 0; i < tokens.length; i += 1) {
    if (tokens[i]!.start >= cursor) break;
    cursorTokenIdx = i;
  }
  if (cursorTokenIdx < 0) return false;

  // Walk back to the unmatched `(` that opens the enclosing list.
  let depth = 0;
  let lparenIdx = -1;
  for (let i = cursorTokenIdx; i >= 0; i -= 1) {
    const t = tokens[i]!;
    if (t.type === 'semicolon') return false;
    if (t.type === 'rparen') {
      depth += 1;
      continue;
    }
    if (t.type === 'lparen') {
      if (depth === 0) {
        lparenIdx = i;
        break;
      }
      depth -= 1;
    }
  }
  if (lparenIdx < 0) return false;
  const intro = identUpper(tokens[lparenIdx - 1]);
  return intro === 'VALUES' || intro === 'VALUE';
}

/**
 * Split the statement's bindings into the MERGE target and its sources.
 *
 * `MERGE INTO <target> [alias] USING <source> [alias]` — the target is named
 * first, so it is the binding with the earliest definition offset. Everything
 * else in scope is a source. Returns `target: undefined` when there are no
 * bindings at all (nothing to qualify against).
 */
export function partitionMergeBindings(bindings: readonly AliasBinding[]): {
  readonly target: AliasBinding | undefined;
  readonly sources: readonly AliasBinding[];
} {
  if (bindings.length === 0) return { target: undefined, sources: [] };
  let target = bindings[0]!;
  for (const b of bindings) {
    if (b.definedAt.start < target.definedAt.start) target = b;
  }
  return { target, sources: bindings.filter((b) => b !== target) };
}

/** One source binding with the column names it projects, as the calling
 *  resolver discovered them. */
export type MergeSourceColumns = {
  readonly binding: AliasBinding;
  readonly columns: readonly string[];
};

/**
 * Add the qualified `<source-alias>.<column>` candidates for a MERGE VALUES
 * element, and apply the D9 ambiguity rule to the bare candidates already in
 * `out`.
 *
 * `formatColumn` is the engine's identifier formatter (PG double-quotes when
 * folding demands it, T-SQL brackets, …) — the alias half uses the binding's
 * VERBATIM source spelling (`aliasSql`), same contract as D8.
 */
export function emitMergeValuesQualifiedColumns(args: {
  readonly out: CompletionCandidate[];
  readonly sources: readonly MergeSourceColumns[];
  readonly targetColumns: readonly string[];
  readonly formatColumn: (name: string) => string;
}): void {
  const { out, sources, targetColumns, formatColumn } = args;
  const targetFolded = new Set(targetColumns.map((c) => c.toLowerCase()));

  const seen = new Set<string>();
  const ambiguous = new Set<string>();
  for (const src of sources) {
    const spelling = src.binding.aliasSql ?? src.binding.alias;
    if (!spelling) continue;
    for (const name of src.columns) {
      const label = `${spelling}.${name}`;
      const key = label.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      if (targetFolded.has(name.toLowerCase())) ambiguous.add(name.toLowerCase());
      out.push({
        label,
        insertText: `${spelling}.${formatColumn(name)}`,
        isSnippet: false,
        kind: 'column',
        tier: 1,
        detail: `column (${src.binding.alias})`,
        filterText: label,
      });
    }
  }

  // D9: a bare name owned by BOTH the MERGE target and a source is an
  // ambiguous column reference — the qualified variants above replace it.
  if (ambiguous.size === 0) return;
  for (let i = out.length - 1; i >= 0; i -= 1) {
    const c = out[i]!;
    if (c.kind !== 'column') continue;
    if (c.label.includes('.')) continue; // already qualified
    if (ambiguous.has(c.label.toLowerCase())) out.splice(i, 1);
  }
}
